import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Sparkles, Bell, Loader2, ChevronRight, Mail } from 'lucide-react';
import { toast } from 'sonner';

const DailyBriefingWidget = () => {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getDailyBriefing();
        setBriefing(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await api.toggleDailyBriefing(!emailEnabled);
      setEmailEnabled(res.data.enabled);
      toast.success(res.data.enabled ? 'Morgon-mejl aktiverat (07:00)' : 'Morgon-mejl avstängt');
    } catch {
      toast.error('Kunde inte ändra');
    } finally { setToggling(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] via-[#1F1F1F] to-[#1A1A1A] text-white rounded-2xl border border-[#E8613A]/20 p-5 relative overflow-hidden" data-testid="daily-briefing-widget">
      <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#E8613A]/15 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#E8613A] rounded-lg flex items-center justify-center">
              <Sparkles size={14} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold">Din AI-briefing</p>
              <p className="text-xs text-white/60">
                {briefing?.stockholm_now ? new Date(briefing.stockholm_now).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'short' }) : '—'}
              </p>
            </div>
          </div>
          <button onClick={handleToggle} disabled={toggling}
            className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 hover:bg-white/15"
            title={emailEnabled ? 'Stäng av morgon-mejl' : 'Slå på morgon-mejl 07:00'}
            data-testid="briefing-toggle-btn"
          >
            {toggling ? <Loader2 size={9} className="animate-spin" /> : <Mail size={9} />}
            {emailEnabled ? '07:00 mejl' : 'Avstängt'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 size={14} className="animate-spin" /> Sammanställer dagens händelser…
          </div>
        ) : !briefing?.bullets?.length ? (
          <p className="text-sm text-white/80">
            <span className="font-bold">Inga akuta händelser idag.</span><br />
            <span className="text-white/60 text-xs">Bra dag att fokusera på nya offerter och kundkontakt.</span>
          </p>
        ) : (
          <ul className="space-y-1" data-testid="briefing-bullets">
            {(briefing.items || briefing.bullets.map((t) => ({ text: t, link: null }))).map((b, i) => (
              <li key={i}>
                {b.link ? (
                  <button
                    type="button"
                    onClick={() => navigate(b.link)}
                    className="w-full flex items-start gap-2 text-sm text-white/90 hover:text-white text-left px-2 py-1.5 -mx-2 rounded-lg hover:bg-white/5 transition-colors group"
                    data-testid={`briefing-item-${i}`}
                  >
                    <Bell size={11} className="text-[#E8613A] flex-shrink-0 mt-1" />
                    <span className="flex-1 leading-snug">{b.text}</span>
                    <ChevronRight size={13} className="text-white/40 flex-shrink-0 mt-0.5 group-hover:text-[#E8613A] group-hover:translate-x-0.5 transition-all" />
                  </button>
                ) : (
                  <div className="flex items-start gap-2 text-sm text-white/90 px-2 py-1.5">
                    <Bell size={11} className="text-[#E8613A] flex-shrink-0 mt-1" />
                    <span className="flex-1 leading-snug">{b.text}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DailyBriefingWidget;
